// src/ConsumerApp/Consumer_app.js

import React, { useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthModalProvider } from './context/AuthModalContext';
import { ToastProvider } from './context/ToastContext';
import { useNavigationExitControl } from './utils/useNavigationExitControl';
import LoginModal from './Screens/Auth/LoginModal';

// ==========================================
// ACTIVE PRODUCTION SCREENS
// ==========================================
import HomeScreen from './Screens/Home/Home';
import SearchScreen from './Screens/Search/SearchScreen';
import ProductsList from './Screens/ProductsList/ProductsList';
import ProductDetailsScreen from './Screens/ProductDetails/ProductDetailsScreen';
import BucketScreen from './Screens/Bucket/Bucket';
import SharedBucketCheckoutScreen from './Screens/Bucket/SharedBucketCheckoutScreen'; // <-- Import the new screen

// Account Management (Master-Detail Layout)
import AccountLayout from './Screens/Account/AccountLayout';
import OrdersMasterDetailView from './Screens/Account/views/OrdersMasterDetailView';
import ProfileContent from './Screens/Account/ProfileContent';
import SupportView from './Screens/Account/views/SupportView';
import OffersView from './Screens/Account/views/OffersView';
import WalletView from './Screens/Account/views/WalletView';
import BrickPointsView from './Screens/Account/views/BrickPointsView';
import PoliciesView from './Screens/Account/views/PoliciesView';
import InvoicesView from './Screens/Account/views/InvoicesView';
import SavedItemsView from './Screens/Account/views/SavedItemsView';
import RfqsView from './Screens/Account/views/RfqsView';

const consumerQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 1000 * 60 * 2,
    },
  },
});

export default function ConsumerApp() {
  useNavigationExitControl();
  const navigate = useNavigate();
  const location = useLocation();

/*   
  useEffect(() => {
    const isProtected = location.pathname.startsWith('/consumer/account');
    const isSharedCheckout = location.pathname.startsWith('/consumer/shared-bucket'); // <-- Identify new protected route
    const storedUser = sessionStorage.getItem('userData') || localStorage.getItem('userData');
    const hasUser = Boolean(storedUser);
    
    // Check for Deep Link parameters
    const params = new URLSearchParams(location.search);
    const rfqId = params.get('rfq_id');
    const targetCustomerId = params.get('customer_id');

    if ((isProtected|| isSharedCheckout) && !hasUser) {
      if (rfqId) {
        // Save intent for after-login redirect
        sessionStorage.setItem('pendingDeepLink', location.pathname + location.search);
      }
      navigate('/consumer/HomeScreen?token=true', { replace: true });
      return;
    } else if ((isProtected|| isSharedCheckout) && hasUser && targetCustomerId) {
      try {
        const currentUser = JSON.parse(storedUser);
        const currentId = currentUser?._id || currentUser?.id;
        
        if (currentId !== targetCustomerId) {
          // Mismatch: Wipe current session, save intent, and force re-login
          localStorage.removeItem('userData');
          sessionStorage.removeItem('userData');
          sessionStorage.removeItem('customerId');
          sessionStorage.removeItem('token');
          sessionStorage.setItem('pendingDeepLink', location.pathname + location.search);
          
          window.dispatchEvent(new Event('auth-state-change'));
          navigate('/consumer/HomeScreen?triggerAuth=true' + location.search, { replace: true });
        }
      } catch (err) {
        console.error("Deep link user validation error", err);
      }
    }
  }, [location.pathname, location.search, navigate]); */

   useEffect(() => {
    const isProtected = location.pathname.startsWith('/consumer/account');
    const isSharedCheckout = location.pathname.startsWith('/consumer/shared-bucket'); // <-- Identify new protected route

    const storedUser = sessionStorage.getItem('userData') || localStorage.getItem('userData');
    const hasUser = Boolean(storedUser);
    
    // Check for Deep Link parameters
    const params = new URLSearchParams(location.search);
    const rfqId = params.get('rfq_id');
    const token = params.get('token');
    const targetCustomerId = params.get('customer_id');

    if (isProtected && !hasUser) {
      if (rfqId) {
        // Save intent for after-login redirect
        sessionStorage.setItem('pendingDeepLink', location.pathname + location.search);
      }
      navigate('/consumer/HomeScreen' + location.search, { replace: true });
    } else if (isProtected && hasUser && targetCustomerId) {
      try {
        const currentUser = JSON.parse(storedUser);
        const currentId = currentUser?._id || currentUser?.id;
        
        if (currentId !== targetCustomerId) {
          // Mismatch: Wipe current session, save intent, and force re-login
          localStorage.removeItem('userData');
          sessionStorage.removeItem('userData');
          sessionStorage.removeItem('customerId');
          sessionStorage.removeItem('token');
          sessionStorage.setItem('pendingDeepLink', location.pathname + location.search);
          
          window.dispatchEvent(new Event('auth-state-change'));
          navigate('/consumer/HomeScreen' + location.search, { replace: true });
        }
      } catch (err) {
        console.error("Deep link user validation error", err);
      }
    }

    if(isSharedCheckout) {
      if (token && !hasUser) {
        // Save intent for after-login redirect
        sessionStorage.setItem('pendingDeepLink', location.pathname + location.search);
        navigate('/consumer/HomeScreen' + location.search, { replace: true });
      }
    }
  }, [location.pathname, location.search, navigate]);
  
  return (
    <QueryClientProvider client={consumerQueryClient}>
      <ToastProvider>
      <AuthModalProvider>
        <Routes>
          {/* Default Redirect to Storefront */}
          <Route path="/" element={<Navigate to="/consumer/HomeScreen" replace />} />
          <Route path="/consumer" element={<Navigate to="/consumer/HomeScreen" replace />} />

          {/* Core Browsing & Commerce (Open to Guests) */}
          <Route path="/consumer/HomeScreen" element={<HomeScreen />} />
          <Route path="/consumer/search" element={<SearchScreen />} />
          <Route path="/consumer/productslist" element={<ProductsList />} />
          <Route path="/consumer/productdetails" element={<ProductDetailsScreen />} />
          <Route path="/consumer/bucket" element={<BucketScreen />} />
          <Route path="/consumer/shared-bucket" element={<SharedBucketCheckoutScreen />} /> {/* New Route */}
          {/* Account Sub-Routes (Protected Workspace) */}
          <Route path="/consumer/account" element={<AccountLayout />}>
           <Route index element={<OrdersMasterDetailView />} />
            <Route path="orders" element={<OrdersMasterDetailView />} />
            <Route path="orders/:orderId" element={<OrdersMasterDetailView />} />
            <Route path="profile" element={<ProfileContent />} />
            <Route path="support" element={<SupportView />} />
            <Route path="offers" element={<OffersView />} />
            <Route path="wallet" element={<WalletView />} />
             <Route path="brick-points" element={<BrickPointsView />} />
            <Route path="policies" element={<PoliciesView />} />
            <Route path="invoices" element={<InvoicesView />} />
            <Route path="saved-items" element={<SavedItemsView />} />
            <Route path="rfqs" element={<RfqsView />} />
          </Route>

          {/* Account Shortcut */}
          <Route
            path="/consumer/AccountScreen"
            element={<Navigate to="/consumer/account/orders" replace />}
          />

          {/* Catch-all Fallback */}
          <Route path="*" element={<Navigate to="/consumer/HomeScreen" replace />} />
        </Routes>

        {/* Global Modal Layer */}
        <LoginModal />
      </AuthModalProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}